import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import {
    addBreadcrumb,
    convertIsoToLocaleString,
    filterDateFormatter,
    makeAPICall,
    setBreadcrumb,
    updateBreadcrumbs,
    validatePermission,
} from '@gokwik/utilities'
import {
    Table,
    Row,
    Col,
    Button,
    Tag,
    Modal,
    Tooltip,
    ColumnProps,
    message,
    PlusCircleOutlined,
    Popover,
    EditOutlined,
} from 'gokwik-ui-kit'
import { previousCustomerFilters } from '@store/filters/selectors'
import { saveFiltersData } from '@store/filters'
import RenderSearchFilters from '@library/components/search-filter'
import { EyeOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import AddDrawer from './addDrawerRoles'
import EditDrawer from './editDrawer'
import { PermissionValues } from '@library/utilities/constants/constants'
const today = dayjs()
const formattedDate = filterDateFormatter(today)

export default function ({ isModalOpen, setModal }) {
    const [total, setTotal] = useState(0)
    const [rolesData, setRolesData] = useState([])
    const prevFilters = useSelector(previousCustomerFilters)
    const [openDrawer, setOpenDrawer] = useState(false)
    const [parameters, setParameters] = useState<any>({
        page: 1,
        sortKey: 'created_at',
        sortOrder: 25,
        pageSize: 25,
    })
    const [customerModalData, setCustomerModalData] = useState(null)
    const [modalOpen, setModalOpen] = useState<any>(false)
    const [showEditModal, setShowEditModal] = useState<any>(false)
    const searchParams = new URL(window.location.href).searchParams
    function titleCase(str) {
        var splitStr = str.toLowerCase().split(' ')
        for (var i = 0; i < splitStr.length; i++) {
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
        }
        return splitStr.join(' ')
    }

    const renderPermissions = (permissions) => {
        const maxVisibleChips = 3
        const visiblePermissions = permissions.slice(0, maxVisibleChips)
        const hiddenPermissions = permissions.slice(maxVisibleChips)

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {visiblePermissions.map((perm, index) => (
                    <Tag key={index} style={{ marginBottom: '4px' }} color='green'>
                        {perm}
                    </Tag>
                ))}
                {hiddenPermissions.length > 0 && (
                    <Popover
                        placement='topLeft'
                        title={'All Permissions'}
                        content={hiddenPermissions.map((each) => (
                            <p> {each} </p>
                        ))}
                    >
                        <p>
                            <span className='text-blue fs12'> ...View more</span>
                        </p>
                    </Popover>
                )}
            </div>
        )
    }
    const columns: ColumnProps<any>[] = [
        {
            title: <span>Name</span>,
            dataIndex: 'name',
            width: 30,
            ellipsis: true,
        },
        {
            title: <span>Permissions</span>,
            dataIndex: 'permissions',
            width: 40,
            ellipsis: true,
            render: (permissions) => renderPermissions(permissions),
        },
        {
            title: <span>Permission Type</span>,
            dataIndex: 'permission_type',
            width: 25,
            ellipsis: true,
            render: (type) => (type === 'web' ? 'Internal Portal' : 'merchant'),
        },
        {
            title: <span>Description</span>,
            dataIndex: 'description',
            width: 30,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span className='truncate text-ellipsis max-w-40 overflow-hidden whitespace-nowrap inline-block'>
                        {text}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: <span>Created At</span>,
            dataIndex: 'created_at',
            width: 30,
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={convertIsoToLocaleString(text)}>{convertIsoToLocaleString(text)}</Tooltip>
            ),
            sorter: (a, b) => (dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? -1 : 1),
            sortDirections: ['ascend', 'descend'],
        },
        ...(validatePermission(PermissionValues?.roles?.edit_roles)
            ? [
                  {
                      title: <span>Actions</span>,
                      dataIndex: 'actions',
                      ellipsis: true,
                      align: 'center' as const,
                      fixed: 'right' as const,
                      width: 15,
                      render: (id, record) => (
                          <Row className='flex gap-3 m-0' justify={'center'}>
                              <Tooltip className=' cursor-pointer' placement='topLeft' title={'Edit Role'}>
                                  <EditOutlined onClick={() => setShowEditModal(record)} className='text-red-500' />
                              </Tooltip>
                          </Row>
                      ),
                  },
              ]
            : []),
    ]

    useEffect(() => {
        updateBreadcrumbs((prev) => [
            prev[0],
            {
                key: 'rolesAndPermissions',
                href: '/general/roles-and-permissions',
                text: 'Roles And Permissions',
            },
        ])
    }, [])

    async function makePermissionsApiCall() {
        try {
            let response = await makeAPICall({
                method: 'get',
                url:
                    process.env.REACT_APP_BASE_URL +
                    '/api/dashboard/roles' +
                    `?is_legacy=${searchParams?.get('is_legacy') ? searchParams.get('is_legacy') : '1'}`,
                params: {
                    ...parameters,
                },
            })

            if (response.data?.data?.roles?.length > 0) {
                setRolesData(response.data?.data?.roles)
                setTotal(response.data?.data?.totalCount)
            } else {
                setRolesData([])
                setTotal(0)
            }
        } catch (err) {
            console.warn(err)
        }
    }

    const reset = (e) => {
        e.preventDefault()
        setParameters({
            page: 1,
            sortKey: 'created_at',
            sortOrder: -1,
            pageSize: 25,
        })
        setTotal(0)
    }
    const handleSearchClick = (e) => {
        e?.preventDefault()
        makePermissionsApiCall()
    }

    useEffect(() => {
        if (Object.keys(prevFilters).length && JSON.stringify(prevFilters) !== JSON.stringify(parameters))
            setParameters({ ...prevFilters })
    }, [prevFilters])

    useEffect(() => {
        if (parameters.page >= 1) {
            makePermissionsApiCall()
        }
    }, [parameters.page])

    const handlePaginationChange: any = (current: number, pageSize?: number) => {
        setParameters((prev) => ({ ...prev, page: current, pageSize }))
    }

    return (
        <>
            <div className='w-full h-full'>
                <div className=' bg-white rounded overflow-clip'>
                    <Table
                        columns={columns}
                        dataSource={rolesData || []}
                        pagination={{
                            current: parameters.page, // Current page number
                            pageSize: parameters.pageSize, // Number of items to display per page
                            total: total, // Total number of items in the data array
                            showSizeChanger: false,
                            onChange: handlePaginationChange,
                            position: ['topLeft', 'bottomLeft'],
                            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                        }}
                    />
                </div>
            </div>
            {isModalOpen === 'Roles' && (
                <AddDrawer
                    title={'Add Role'}
                    open={!!isModalOpen}
                    onClose={(refetch: boolean) => {
                        setModal(null)
                        makePermissionsApiCall()
                    }}
                />
            )}
            {showEditModal && (
                <EditDrawer
                    title={'Update Role'}
                    open={!!showEditModal}
                    onClose={(refetch: boolean) => {
                        setShowEditModal(false)
                        makePermissionsApiCall()
                    }}
                    recordData={showEditModal}
                />
            )}
        </>
    )
}
